import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import MultiselectItem from 'Components/Molecules/Multiselect/MultiselectItem';
import { useState } from 'react';

export default function MultiselectInvoiceTaxesContent(props) {
    const { groups, className, value = [], onChange } = props;

    const [retention, setRetention] = useState(() => {
        if (!Array.isArray(value)) return null;

        const retentions = groups.flatMap((g) => g.taxes).filter((t) => t.is_retention);

        const retentionId = retentions.find((retentionTax) => {
            return value.includes(retentionTax.id);
        })?.id;

        return retentionId || null;
    });

    const [iva, setIva] = useState(() => {
        if (!Array.isArray(value)) return null;

        const ivas = groups.flatMap((g) => g.taxes).filter((t) => !t.is_retention);
        const ivaId = ivas.find((ivaTax) => {
            return value.includes(ivaTax.id);
        })?.id;

        return ivaId || null;
    });

    const [currentGroup, setCurrentGroup] = useState(() => {
        const hasSelected = Array.isArray(value) && value.length > 0;
        const defaultId = groups[0]?.id ?? null;

        if (!hasSelected) {
            return defaultId;
        }

        const firstSelectedGroup = groups.find((group) =>
            value.find((id) => {
                const tax = group.taxes.find((t) => t.id === id);
                return !!tax;
            }),
        );

        return firstSelectedGroup?.id ?? defaultId;
    });

    const handleSelectTax = (tax) => {
        if (tax.is_retention) {
            setRetention(tax.id);
            onChange([tax.id, iva].filter(Boolean));
        } else {
            setIva(tax.id);
            onChange([tax.id, retention].filter(Boolean));
        }
    };

    const handleDeselectTax = (tax) => {
        if (tax.is_retention) {
            setRetention(null);
            onChange([iva].filter(Boolean));
        } else {
            setIva(null);
            onChange([retention].filter(Boolean));
        }
    };

    const items = groups.find((g) => g.id === currentGroup)?.taxes ?? [];

    return (
        <div>
            <div
                className={cn(
                    'sticky top-0 z-10 flex items-center gap-0.5 py-1 px-1.5 bg-gray-100 overflow-x-auto scrollbar-thin',
                    className,
                )}
            >
                {groups.map((group) => (
                    <button
                        type="button"
                        key={group.id}
                        className={cn(
                            'whitespace-nowrap text-xs px-3 py-1.5 font-semibold rounded-sm',
                            currentGroup === group.id && 'font-semibold bg-white text-dark-blue shadow',
                        )}
                        onClick={(ev) => {
                            setCurrentGroup(group.id);
                            const target = ev.currentTarget;
                            target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                        }}
                    >
                        {group.name}
                    </button>
                ))}
            </div>

            <div className="p-1.5 min-h-12">
                {items.map((tax) => (
                    <MultiselectItem
                        key={tax.id}
                        checked={value.includes(tax.id)}
                        onChange={(ev) => {
                            const isAdding = ev.target.checked;
                            const action = isAdding ? handleSelectTax : handleDeselectTax;
                            action(tax);
                        }}
                    >
                        {tax.name}
                    </MultiselectItem>
                ))}
            </div>
        </div>
    );
}

MultiselectInvoiceTaxesContent.propTypes = {
    value: PropTypes.arrayOf(PropTypes.string),
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    groups: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            taxes: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    name: PropTypes.string.isRequired,
                }),
            ).isRequired,
        }),
    ).isRequired,
};
